// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { useMlModels, type MlModel } from './useMlModels';

/**
 * The model registry, for the admin analysis page: every trained detector (and the classical
 * thresholds it is measured against) with its latest precision / recall / F1 per feature
 * kind, so two versions can be read side by side. Read-only; `training/*.py --register`
 * writes it.
 */

const KINDS = ['bunker', 'water', 'green', 'fairway', 'trees'];

export function MlModelsPanel({
  apiBaseUrl,
  token,
}: {
  apiBaseUrl: string;
  token: string | null | undefined;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { models, datasets, loading, error } = useMlModels(apiBaseUrl, token, open);

  if (!open) {
    return (
      <Pressable
        accessibilityRole="button"
        testID="ml-models-open"
        style={styles.openButton}
        onPress={() => setOpen(true)}
      >
        <Text style={styles.openButtonText}>{t('mlModels.open')}</Text>
      </Pressable>
    );
  }

  return (
    <View style={styles.card} testID="ml-models-panel">
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('mlModels.title')}</Text>
        <Pressable accessibilityRole="button" onPress={() => setOpen(false)}>
          <Text style={styles.close}>{t('mlModels.close')}</Text>
        </Pressable>
      </View>

      {loading ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.notice}>{t('mlModels.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.notice}>{t('mlModels.failed')}</Text>
      ) : models.length === 0 ? (
        <Text style={styles.notice} testID="ml-models-empty">
          {t('mlModels.empty')}
        </Text>
      ) : (
        <>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, styles.modelCell, styles.headerText]}>{t('mlModels.model')}</Text>
            {KINDS.map((kind) => (
              <Text key={kind} style={[styles.cell, styles.headerText]}>
                {t(`courseAnalysis.kind.${kind}`)}
              </Text>
            ))}
          </View>
          {models.map((model) => (
            <ModelRow key={model.id ?? 'classical'} model={model} />
          ))}
          <Text style={styles.legend}>{t('mlModels.legend')}</Text>
          {datasets.length > 0 ? (
            <Text style={styles.legend}>{t('mlModels.datasets', { count: datasets.length })}</Text>
          ) : null}
        </>
      )}
    </View>
  );
}

function ModelRow({ model }: { model: MlModel }) {
  const { t } = useI18n();
  const byKind = new Map(model.latestEvaluations.map((e) => [e.kind, e]));
  return (
    <View style={styles.row} testID={`ml-model-${model.name}-${model.version}`}>
      <View style={[styles.cell, styles.modelCell]}>
        <Text style={styles.modelName}>{model.name}</Text>
        <Text style={styles.modelMeta}>{model.version}</Text>
        {model.trainedAt ? <Text style={styles.modelMeta}>{formatWhen(model.trainedAt)}</Text> : null}
        {model.datasetDescription ? <Text style={styles.modelMeta}>{model.datasetDescription}</Text> : null}
      </View>
      {KINDS.map((kind) => {
        const score = byKind.get(kind);
        return (
          <Text key={kind} style={styles.cell}>
            {score
              ? t('mlModels.score', {
                  p: score.precision.toFixed(2),
                  r: score.recall.toFixed(2),
                  f1: score.f1.toFixed(2),
                })
              : '–'}
          </Text>
        );
      })}
    </View>
  );
}

const formatWhen = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
};

const styles = StyleSheet.create({
  openButton: {
    alignSelf: 'stretch',
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: '#07200D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  openButtonText: {
    color: appColors.accent.primary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  card: {
    backgroundColor: '#07200D',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 14,
    gap: 10,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  close: { color: appColors.accent.primary, fontSize: 12, fontWeight: '700' },
  notice: { color: appColors.text.secondary, fontSize: 13 },
  tableHeader: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#1A3A20', paddingBottom: 6 },
  headerText: { color: appColors.text.secondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 },
  row: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#0F2A15' },
  cell: { flex: 1, color: appColors.text.primary, fontSize: 12 },
  modelCell: { flex: 2 },
  modelName: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  modelMeta: { color: appColors.text.secondary, fontSize: 11 },
  legend: { color: appColors.text.secondary, fontSize: 11 },
});
